import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Send, Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { trackCTA } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";

const ContactForm = () => {
  const [form, setForm] = useState({
    naam: "",
    bedrijf: "",
    telefoon: "",
    email: "",
    vraag: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    const { error } = await supabase.from("contact_messages").insert({
      naam: form.naam.trim(),
      bedrijf: form.bedrijf.trim() || null,
      telefoon: form.telefoon.trim() || null,
      email: form.email.trim(),
      vraag: form.vraag.trim(),
    });
    if (error) {
      setStatus("error");
      return;
    }
    trackCTA("Verstuur contactformulier", "contact_form");
    setStatus("success");
    setForm({ naam: "", bedrijf: "", telefoon: "", email: "", vraag: "" });
  };

  return (
    <div className="grid gap-12 lg:grid-cols-2">
      {/* Info */}
      <div className="space-y-8">
        <div>
          <h2 className="text-3xl font-bold md:text-4xl">
            Liever even <span className="text-gradient">bellen?</span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Wij nemen gewoon de telefoon op. Geen keuzemenu, geen wachtrij. Je krijgt direct een techneut aan de lijn die je meteen helpt.
          </p>
        </div>

        <div className="space-y-4">
          <a
            href="tel:+31858223400"
            onClick={() => trackCTA("Bel ons", "contact_info")}
            className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-card transition-all hover:shadow-card-hover"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rinqel-blue-gradient">
              <Phone className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold">Bel ons</p>
              <p className="text-sm text-muted-foreground">085-8223400</p>
            </div>
          </a>
          <a
            href="https://api.whatsapp.com/send?phone=31858223400"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTA("WhatsApp", "contact_info")}
            className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-card transition-all hover:shadow-card-hover"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rinqel-green-gradient">
              <MessageCircle className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold">WhatsApp</p>
              <p className="text-sm text-muted-foreground">085-8223400</p>
            </div>
          </a>
          <a
            href="mailto:contact@rinqel.it"
            onClick={() => trackCTA("Mail ons", "contact_info")}
            className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-card transition-all hover:shadow-card-hover"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: "linear-gradient(135deg, #eb4f05, #ff7b3a)" }}>
              <Mail className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold">Mail ons</p>
              <p className="text-sm text-muted-foreground">contact@rinqel.it</p>
            </div>
          </a>
          <a
            href="https://maps.app.goo.gl/fjCgnkUTePEbzge27"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-card transition-all hover:shadow-card-hover"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rinqel-blue-gradient">
              <MapPin className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold">Bezoek ons</p>
              <p className="text-sm text-muted-foreground">Rijksweg 42a, 1906 BJ Limmen</p>
            </div>
          </a>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="rounded-xl border border-border bg-card p-6 shadow-card md:p-8">
        <h3 className="mb-6 text-xl font-semibold">Stel je vraag</h3>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Naam *</label>
              <input
                type="text"
                required
                maxLength={100}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.naam}
                onChange={(e) => setForm({ ...form, naam: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Bedrijfsnaam</label>
              <input
                type="text"
                maxLength={100}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.bedrijf}
                onChange={(e) => setForm({ ...form, bedrijf: e.target.value })}
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Telefoon</label>
              <input
                type="tel"
                maxLength={20}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.telefoon}
                onChange={(e) => setForm({ ...form, telefoon: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">E-mail *</label>
              <input
                type="email"
                required
                maxLength={255}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">Je vraag *</label>
            <textarea
              required
              rows={4}
              maxLength={1000}
              className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={form.vraag}
              onChange={(e) => setForm({ ...form, vraag: e.target.value })}
            />
          </div>
          <Button variant="hero" size="lg" className="w-full" type="submit" disabled={status === "sending"}>
            <Send className="mr-1 h-4 w-4" />
            {status === "sending" ? "Versturen..." : "Verstuur"}
          </Button>
          <div aria-live="polite">
            {status === "success" && (
              <p className="rounded-lg border border-border bg-muted p-3 text-sm">
                Bedankt voor je bericht! We nemen zo snel mogelijk contact met je op.
              </p>
            )}
            {status === "error" && (
              <p className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                Er ging iets mis bij het versturen. Probeer het nog eens, of bel ons op 085-8223400.
              </p>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
